import React from 'react';
import { motion } from 'framer-motion';
import type { CollectionUnlock } from '../types';
import { iconFor } from '../lib/utils';
import { CONSTANTS } from '../data.js';

type CollectionUnlockPopupProps = {
  collection: CollectionUnlock;
  onClose: () => void;
};

export const CollectionUnlockPopup: React.FC<CollectionUnlockPopupProps> = ({ collection, onClose }) => {
  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(26, 22, 17, 0.85)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative flex flex-col items-center p-8 rounded-xl w-[420px] max-w-[90vw]"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.success}`,
          boxShadow: `${CONSTANTS.ui.theme.effects.ornateBoxShadow}, 0 0 20px rgba(34, 139, 34, 0.3)`
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Decorative corners */}
        <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 rounded-tl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.success }} />
        <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 rounded-tr-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.success }} />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 rounded-bl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.success }} />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 rounded-br-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.success }} />

        <motion.div 
          className="font-cinzel text-2xl font-bold mb-4"
          style={{ 
            color: CONSTANTS.ui.theme.colors.success,
            textShadow: '0 0 10px rgba(34, 139, 34, 0.5)'
          }}
        >
          Collection Complete!
        </motion.div>
        
        <motion.div 
          className="font-cinzel text-xl font-semibold text-center mb-4"
          style={{ color: CONSTANTS.ui.theme.colors.text }}
        >
          {collection.name}
        </motion.div>

        <motion.div 
          className="font-crimson text-sm text-center mb-6 leading-relaxed"
          style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
        >
          {collection.lore}
        </motion.div>

        <div className="font-cinzel text-sm font-bold mb-2" style={{ color: CONSTANTS.ui.theme.colors.primary }}>Avatar Unlocked!</div>
        <motion.div
          className="rounded-xl mb-4 flex items-center justify-center overflow-hidden"
          style={{
            width: 100,
            height: 100,
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary}20 0%, ${CONSTANTS.ui.theme.colors.accent}20 100%)`,
            border: `2px solid ${CONSTANTS.ui.theme.colors.primary}`,
            boxShadow: `inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(218, 165, 32, 0.2)`
          }}
        >
          <img
            src={iconFor(collection.avatarKey)}
            alt="Unlocked Avatar"
            className="object-contain max-w-full max-h-full p-2"
          />
        </motion.div>
        
        <motion.button
          onClick={onClose}
          className="font-crimson mt-4 w-full px-8 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
            color: CONSTANTS.ui.theme.colors.background,
            border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
          }}
        >
          Awesome!
        </motion.button>
      </motion.div>
    </motion.div>
  );
};
